/**
 * 機台服務帳號 token（EDC / 票券 QR / 語音端點共用）。
 *
 * 帳密不在這個檔案，也不在任何前端 bundle 裡——`/kiosk-session/token` 由本機服務
 * （`kiosk-account-plugin.ts` → `scripts/kiosk-account-handler.mjs`）代理登入，帳密只存在
 * Node 端與該機台本機的設定檔。`login()` 只負責拿一把短期 token。
 *
 * 帳號本身的後端設定沒變：角色 `kiosk_terminal`，只有 `tickets.read` 一個權限（migration
 * `20260826010000_seed_kiosk_terminal_role`）。dev / test 要先跑 `pnpm db:seed`
 * （帳密由 `apps/api/scripts/seed-dev-fixtures.ts` 建，不在 migration 裡——migration 會在
 * 正式環境執行，不該種已知密碼的帳號）。沒種的症狀是刷卡停在「售票機登入失敗」，QR 也
 * 不顯示、印不出來。正式環境的帳密透過機台本機的設定頁輸入，不是建置時的環境變數。
 */

let pending: Promise<string | null> | null = null;
const CHANGE_KEY = 'kiosk-account-updated';

export function resetServiceToken(notify = false): void {
  pending = null;
  if (notify && typeof window !== 'undefined') {
    try {
      localStorage.setItem(CHANGE_KEY, crypto.randomUUID());
    } catch {
      /* memory still reset */
    }
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === CHANGE_KEY) resetServiceToken();
  });
}

async function login(): Promise<string | null> {
  try {
    const response = await fetch('/kiosk-session/token', {
      method: 'POST',
      credentials: 'omit',
      redirect: 'error',
      headers: { 'X-Kiosk-Request': '1' },
      signal: AbortSignal.timeout(15000),
    });
    if (!response.ok) return null;
    const result = (await response.json()) as { access_token?: unknown };
    return typeof result.access_token === 'string' && result.access_token
      ? result.access_token
      : null;
  } catch {
    return null;
  }
}

export async function ensureServiceToken(): Promise<string | null> {
  for (;;) {
    const request = (pending ??= login());
    const token = await request;
    // A save can finish while a previous login is still in flight.
    if (pending !== request) continue;
    if (!token) pending = null;
    return token;
  }
}

export async function rejectServiceToken(token: string): Promise<void> {
  const request = pending;
  if (request && (await request) === token && pending === request) pending = null;
}
